import { useCallback, useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import {
  COMMENTS_PAGE_SIZE,
  getCommentChallenge,
  listComments,
  postComment,
  type PublicComment,
} from "@/lib/comments.functions";

function formatDate(iso: string) {
  try {
    return new Date(iso).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

/** Fil de commentaires public affiché sous chaque vidéo. */
export function CommentThread({
  pageKey,
  videoTitle,
}: {
  pageKey: string;
  videoTitle?: string;
}) {
  const load = useServerFn(listComments);
  const send = useServerFn(postComment);
  const loadChallenge = useServerFn(getCommentChallenge);

  const [comments, setComments] = useState<PublicComment[] | null>(null);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [challenge, setChallenge] = useState<{ question: string; token: string } | null>(null);
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  const [website, setWebsite] = useState("");
  const [authorName, setAuthorName] = useState("");
  const [body, setBody] = useState("");
  const [state, setState] = useState<"idle" | "sending">("idle");
  const [feedback, setFeedback] = useState("");

  const refreshChallenge = useCallback(async () => {
    try {
      const c = await loadChallenge();
      setChallenge(c);
      setCaptchaAnswer("");
    } catch {
      setChallenge(null);
    }
  }, [loadChallenge]);

  const refreshComments = useCallback(
    async (keep = 0) => {
      try {
        const limit = Math.max(keep, COMMENTS_PAGE_SIZE);
        const page = await load({ data: { pageKey, offset: 0, limit } });
        setComments(page.items);
        setTotal(page.total);
        setHasMore(page.hasMore);
      } catch {
        setComments([]);
        setTotal(0);
        setHasMore(false);
      }
    },
    [load, pageKey],
  );

  const loadMore = useCallback(async () => {
    if (!comments) return;
    setLoadingMore(true);
    try {
      const page = await load({
        data: { pageKey, offset: comments.length, limit: COMMENTS_PAGE_SIZE },
      });
      setComments((prev) => [...(prev ?? []), ...page.items]);
      setTotal(page.total);
      setHasMore(page.hasMore);
    } catch {
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  }, [comments, load, pageKey]);

  useEffect(() => {
    void refreshComments();
    void refreshChallenge();
  }, [refreshComments, refreshChallenge]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setState("sending");
    setFeedback("");
    try {
      const res = await send({
        data: {
          pageKey,
          pagePath: typeof window === "undefined" ? "" : window.location.pathname,
          videoTitle: videoTitle ?? "",
          authorName,
          body,
          captchaToken: challenge?.token ?? "",
          captchaAnswer,
          website,
        },
      });
      setFeedback(res.message);
      if (res.ok) {
        setBody("");
        await refreshComments((comments?.length ?? 0) + 1);
      }
      await refreshChallenge();
    } catch {
      setFeedback("Vérifiez votre pseudo (2 caractères minimum) et un message d'au moins 5 caractères.");
    } finally {
      setState("idle");
    }
  };

  const input =
    "w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-foreground";

  return (
    <section id="commentaires" className="mt-10 border-t border-border pt-8">
      <h2 className="font-[family-name:var(--font-display)] text-2xl">
        Commentaires{comments ? ` (${total})` : ""}
      </h2>
      <p className="mt-2 text-sm text-muted-foreground">
        Partagez un souvenir, une précision historique ou une source sur ce reportage. Les
        commentaires sont modérés.
      </p>

      {comments === null ? (
        <p className="mt-6 text-sm text-muted-foreground">Chargement du fil…</p>
      ) : comments.length === 0 ? (
        <p className="mt-6 text-sm text-muted-foreground">
          Aucun commentaire pour le moment — soyez le premier à réagir.
        </p>
      ) : (
        <ul className="mt-6 space-y-4">
          {comments.map((c) => (
            <li key={c.id} className="rounded-xl border border-border bg-card p-4">
              <div className="flex flex-wrap items-baseline gap-2">
                <span className="text-sm font-semibold">{c.authorName}</span>
                <span className="text-[0.65rem] uppercase tracking-[0.2em] text-muted-foreground">
                  {formatDate(c.createdAt)}
                </span>
              </div>
              <p className="mt-2 whitespace-pre-line text-[0.95rem] leading-relaxed text-muted-foreground">
                {c.body}
              </p>
            </li>
          ))}
        </ul>
      )}

      {comments && comments.length > 0 ? (
        <div className="mt-5 flex flex-wrap items-center gap-3">
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
            {comments.length} / {total} affichés
          </p>
          {hasMore ? (
            <button
              type="button"
              onClick={() => void loadMore()}
              disabled={loadingMore}
              className="rounded-full border border-border px-4 py-1.5 text-sm transition-colors hover:bg-accent disabled:opacity-50"
            >
              {loadingMore ? "Chargement…" : `Voir ${Math.min(COMMENTS_PAGE_SIZE, total - comments.length)} commentaires de plus`}
            </button>
          ) : null}
        </div>
      ) : null}

      <form onSubmit={onSubmit} className="mt-8 space-y-3 rounded-xl border border-border p-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block text-sm">
            <span className="mb-1 block text-[0.65rem] uppercase tracking-[0.2em] text-muted-foreground">
              Pseudo
            </span>
            <input
              className={input}
              value={authorName}
              onChange={(e) => setAuthorName(e.target.value)}
              maxLength={60}
              required
            />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block text-[0.65rem] uppercase tracking-[0.2em] text-muted-foreground">
              {challenge?.question ?? "Question anti-spam"}
            </span>
            <input
              className={input}
              value={captchaAnswer}
              onChange={(e) => setCaptchaAnswer(e.target.value)}
              inputMode="numeric"
              required
            />
          </label>
        </div>
        <label className="block text-sm">
          <span className="mb-1 block text-[0.65rem] uppercase tracking-[0.2em] text-muted-foreground">
            Votre commentaire
          </span>
          <textarea
            className={`${input} min-h-28`}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={2000}
            required
          />
        </label>
        <input
          type="text"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          className="hidden"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
        />
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="submit"
            disabled={state === "sending"}
            className="rounded-full border border-foreground px-5 py-2 text-sm font-medium transition-colors hover:bg-foreground hover:text-background disabled:opacity-50"
          >
            {state === "sending" ? "Envoi…" : "Publier"}
          </button>
          {feedback ? <span className="text-sm text-muted-foreground">{feedback}</span> : null}
        </div>
        <p className="text-xs text-muted-foreground">
          Pas d'adresse e-mail demandée, pas de liens acceptés. Les propos injurieux sont supprimés.
        </p>
      </form>
    </section>
  );
}
